import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";

export default function Forside() {
  const [pageData, setPageData] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const containerRef = useRef(null);
  
  // Helper to get initial dimensions based on window width to avoid layout shift/double loading on mount
  const getInitialDimensions = () => {
    if (typeof window === "undefined") return { width: 360, height: 620 };
    const width = window.innerWidth;
    if (width < 640) {
      // Mobile: container is w-[320px], screen is ~280px wide, ~556px high
      return { width: 280, height: 556 };
    } else if (width < 1024) {
      // sm: container is w-[380px], screen is ~340px wide, ~636px high
      return { width: 340, height: 636 };
    } else {
      // lg: container is w-[400px], screen is ~360px wide, ~676px high
      return { width: 360, height: 676 };
    }
  };

  const [iframeDimensions, setIframeDimensions] = useState(getInitialDimensions);

  useEffect(() => {
    if (!containerRef.current) return;

    let timeoutId = null;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        
        // Clear any existing timeout to debounce
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
          // Facebook Page plugin has a min-width of 180px and max-width of 500px.
          const clampedWidth = Math.min(Math.max(Math.floor(width), 180), 500);
          const clampedHeight = Math.floor(height);
          
          setIframeDimensions({ width: clampedWidth, height: clampedHeight });
        }, 150);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    // Set document title and meta description
    document.title = "Havneloppen";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Besøg Havneloppen i Nyborg - dit lokale loppemarked med unikke loppefund, genbrug, retro møbler, tøj, porcelæn og keramik. Se vores nyeste fund her."
      );
    }

    // Fetch page data and products in parallel
    Promise.all([
      fetch("https://api.dev.auxo.dk/public/content/sider").then((res) => res.json()),
      fetch("https://api.dev.auxo.dk/public/content/produkter").then((res) => res.json()),
    ])
      .then(([sider, produkter]) => {
        if (Array.isArray(sider)) {
          const page = sider.find((p) => p.url_slug === "forside");
          setPageData(page);
        }
        if (Array.isArray(produkter)) {
          // Filter featured products
          const featured = produkter.filter((p) => p.fremhaevet);
          setFeaturedProducts(featured);
        }
      })
      .catch((err) => console.error("Error loading home page data:", err))
      .finally(() => setLoading(false));
  }, []);

  const categories = [
    {
      titel: "Billeder/film og lyd",
      slug: "billeder-film-og-lyd",
      icon: "fa-solid fa-compact-disc",
      desc: "Vinylplader, CD'er, klassiske film, bånd og fine retro billeder.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/734152620-2834377060295658-7962920837030114829-n.jpg",
    },
    {
      titel: "Børnenes univers",
      slug: "bornenes-univers",
      icon: "fa-solid fa-shapes",
      desc: "Retro legetøj, børnebøger, spil og sjove sager til de kære børn.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/4718_37e36a14.jpg",
    },
    {
      titel: "Design",
      slug: "design",
      icon: "fa-solid fa-gem",
      desc: "Klassisk dansk design, Holmegaard glas, retroikoner og tidløs æstetik.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/733756931-1877597589603934-751592285414870833-n.jpg",
    },
    {
      titel: "Elektonik",
      slug: "elektonik",
      icon: "fa-solid fa-radio",
      desc: "Radioer, hi-fi, retro apparater, højttalere og spændende gadgets.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/732165215-942734548785218-8810224675476313418-n.jpg",
    },
    {
      titel: "Lamper",
      slug: "lamper",
      icon: "fa-solid fa-lightbulb",
      desc: "Flot retro belysning, PH lamper, pendler, bord- og gulvlamper.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/produkt-ph-lampe.jpg",
    },
    {
      titel: "Læsestof",
      slug: "laesestof",
      icon: "fa-solid fa-book-open",
      desc: "Masser af blandet læsestof, bøger, blade og tegneserier til bundpriser.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/4795.jpg",
    },
    {
      titel: "Malerier",
      slug: "malerier",
      icon: "fa-solid fa-palette",
      desc: "Smukke malerier, litografier, tryk og unik vægkunst med historie.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/736191609-1346679517572804-4367463417409995182-n.jpg",
    },
    {
      titel: "Møbler",
      slug: "mobler",
      icon: "fa-solid fa-chair",
      desc: "Retro teaktræsmøbler, vintage stole og hyggelige borde med sjæl.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/733810396-1025372103515684-8960435062557452912-n.jpg",
    },
    {
      titel: "Porcelæn og keramik",
      slug: "porcelan-og-keramik",
      icon: "fa-solid fa-mug-saucer",
      desc: "Dansk porcelæn, Royal Copenhagen, stentøj og retro keramikvaser.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/734039423-1000751439610249-8970775704282730051-n.jpg",
    },
    {
      titel: "Småting til hjemmet",
      slug: "smating-til-hjemmet",
      icon: "fa-solid fa-heart",
      desc: "Hyggelig brugskunst, nips, vaser, stager og små skatte til boligen.",
      image: "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/733955239-1493264768793475-6410008464904750208-n.jpg",
    },
  ];

  // Optimize hero image URL
  const heroImage = pageData?.billede?.[0] || "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/732789416-3930006427136378-8236971886744770619-n.jpg";
  const optimizedHeroImage = heroImage.replace(
    "assets.auxodev.dk/assets",
    "assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets"
  );

  return (
    <div className="flex flex-col min-h-screen">
      {/* Top Opening Hours Bar */}
      <div className="bg-[#f26a36] text-brand-blue py-2.5 px-6 text-center text-xs md:text-sm font-medium border-b border-brand-orange-hover">
        <div className="mx-auto max-w-7xl flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
          <span className="flex items-center gap-2">
            <i className="fa-solid fa-clock text-brand-blue" />
            <span>Åbent i dag: <strong className="text-brand-blue font-bold">Mandag-Torsdag 10-18</strong> & <strong className="text-brand-blue font-bold">Lørdag-Søndag 10-16</strong></span>
          </span>
          <span className="hidden md:inline text-brand-blue/30">|</span>
          <span className="flex items-center gap-2">
            <i className="fa-solid fa-location-dot text-brand-blue" />
            <span>Knudshovedvej 38, 5800 Nyborg</span>
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-brand-blue text-warm-white overflow-hidden py-20 md:py-28 lg:py-32">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedHeroImage}
            alt="Havneloppen i Nyborg"
            className="h-full w-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/90 to-brand-blue/40" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="max-w-2xl lg:max-w-3xl">
            <span className="inline-block rounded-full bg-brand-orange/20 border border-brand-orange/30 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-orange mb-6">
              <i className="fa-solid fa-anchor mr-2" />
              Nyborgs hyggeligste loppemarked
            </span>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-warm-white sm:text-5xl md:text-6xl leading-tight mb-6">
              Havneloppen i Nyborg
            </h1>
            <p className="text-lg md:text-xl text-warm-white/85 leading-relaxed mb-8 max-w-2xl">
              Gå på opdagelse i vores levende butik fyldt med unikke loppefund, retro møbler, klassisk dansk design, lamper, læsestof samt porcelæn og keramik. Vi får nye varer hver eneste uge!
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/kategorier"
                className="rounded-xl bg-brand-orange px-6 py-3.5 text-base font-bold text-warm-white shadow-lg shadow-brand-orange/20 hover:bg-brand-orange-hover transition-all duration-200 hover:-translate-y-0.5"
              >
                Se alle varer
                <i className="fa-solid fa-arrow-right ml-2" />
              </Link>
              <Link
                to="/kontakt"
                className="rounded-xl border-2 border-warm-white/20 bg-brand-blue/40 px-6 py-3.5 text-base font-bold text-warm-white backdrop-blur-sm hover:bg-brand-blue-hover hover:border-warm-white/40 transition-all duration-200"
              >
                Kontakt butikken
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-24 bg-warm-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
            <div className="space-y-6">
              <h2 className="font-display text-3xl font-bold tracking-tight text-brand-blue sm:text-4xl">
                En levende butik med sjæl og personlighed
              </h2>
              <div
                className="prose text-dark-graphite-light"
                dangerouslySetInnerHTML={{ __html: pageData?.indhold || "" }}
              />
              <div className="pt-4 flex flex-wrap gap-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                    <i className="fa-solid fa-check" />
                  </div>
                  <span className="text-sm font-semibold text-dark-graphite">Konstant skiftende varer</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                    <i className="fa-solid fa-check" />
                  </div>
                  <span className="text-sm font-semibold text-dark-graphite">Gode, lokale priser</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                    <i className="fa-solid fa-check" />
                  </div>
                  <span className="text-sm font-semibold text-dark-graphite">Personlig betjening</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-4/3 overflow-hidden rounded-2xl border border-warm-beige shadow-lg">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/631586353-1484986060296776-2960624181118375474-n.jpg"
                  alt="Havneloppen butik"
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Decorative badge */}
              <div className="absolute -bottom-6 -right-6 hidden sm:flex flex-col items-center justify-center rounded-2xl bg-brand-blue text-brand-orange p-5 shadow-xl border border-brand-blue-hover">
                <span className="font-mono text-3xl font-extrabold leading-none">100%</span>
                <span className="text-xs uppercase tracking-widest font-bold mt-1">Ægte Genbrug</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Curated Categories Section */}
      <section className="py-16 md:py-24 bg-warm-beige/40 border-y border-warm-beige">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-3xl font-bold tracking-tight text-brand-blue sm:text-4xl mb-4">
              Udforsk vores varekategorier
            </h2>
            <p className="text-dark-graphite-light">
              Vi har inddelt butikken i overskuelige afdelinger, så du nemt kan gå på opdagelse og finde lige det, der passer til dig.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/${cat.slug}`}
                className="group relative flex flex-col overflow-hidden rounded-xl border border-warm-beige bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              >
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg bg-warm-beige mb-3 relative">
                  <img
                    src={cat.image.replace("assets.auxodev.dk/assets", "assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets")}
                    alt={cat.titel}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-brand-blue/10 group-hover:bg-brand-blue/0 transition-colors" />
                </div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                    <i className={`${cat.icon} text-xs`} />
                  </div>
                  <h3 className="font-display text-sm font-bold text-brand-blue group-hover:text-brand-orange transition-colors truncate">
                    {cat.titel}
                  </h3>
                </div>
                <p className="text-xs text-dark-graphite-light leading-relaxed flex-1 line-clamp-3">
                  {cat.desc}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section ("Nye fund i butikken") */}
      <section className="py-16 md:py-24 bg-warm-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-brand-blue sm:text-4xl mb-3">
                Nye fund i butikken
              </h2>
              <p className="text-dark-graphite-light max-w-xl">
                Her er et udvalg af de nyeste loppefund, der lige er landet på hylderne. Vi sælger kun direkte fra butikken i Nyborg – kig forbi før det er væk!
              </p>
            </div>
            <Link
              to="/kategorier"
              className="inline-flex w-fit items-center gap-2 rounded-lg border-2 border-brand-blue px-5 py-2.5 text-sm font-bold text-brand-blue hover:bg-brand-blue hover:text-warm-white transition-all duration-200"
            >
              Se alle varer
              <i className="fa-solid fa-arrow-right" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <i className="fa-solid fa-spinner animate-spin text-3xl text-brand-orange" />
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="text-center py-12 text-dark-graphite-light">
              Ingen fremhævede produkter fundet i øjeblikket.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {featuredProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Facebook Focus Section */}
      <section className="py-16 md:py-24 bg-brand-blue text-warm-white relative overflow-hidden">
        {/* Decorative background anchor icon */}
        <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 opacity-5 pointer-events-none">
          <i className="fa-brands fa-facebook text-[400px]" />
        </div>

        <div className="mx-auto max-w-7xl px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Text Content */}
            <div className="lg:col-span-7 text-center lg:text-left space-y-6">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-warm-white/10 text-brand-orange">
                <i className="fa-brands fa-facebook text-3xl" />
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-warm-white sm:text-4xl">
                Følg vores daglige fund på Facebook!
              </h2>
              <p className="text-lg text-warm-white/85 leading-relaxed">
                Havneloppen har stor aktivitet og succes på Facebook. Da vi får nye, spændende loppefund ind hver eneste dag, lægger vi løbende billeder og beskrivelser op direkte på vores Facebook-side. Her kan du hurtigt reservere eller spørge ind til varerne, før de bliver solgt i butikken!
              </p>
              <div className="flex justify-center lg:justify-start pt-2">
                <a
                  href="https://www.facebook.com/p/Havneloppen-100063561880040/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 rounded-xl bg-brand-orange px-8 py-4 text-base font-bold text-warm-white shadow-lg shadow-brand-orange/30 hover:bg-brand-orange-hover transition-all duration-200 hover:-translate-y-0.5"
                >
                  <i className="fa-brands fa-facebook text-xl" />
                  Besøg os på Facebook nu
                </a>
              </div>
            </div>

            {/* Floating iPhone X Column */}
            <div className="lg:col-span-5 flex justify-center relative h-[450px] lg:h-[500px] overflow-visible">
              {/* Floating Container */}
              <div className="absolute bottom-[-220px] lg:bottom-[-260px] w-[320px] sm:w-[380px] lg:w-[400px] h-[580px] sm:h-[660px] lg:h-[700px] bg-slate-950 rounded-[45px] p-3 shadow-2xl border-[8px] border-slate-800 ring-4 ring-slate-700/50 overflow-hidden transform-gpu will-change-transform transition-all duration-300">
                {/* iPhone Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 h-5 w-32 bg-slate-950 rounded-b-2xl z-30 flex items-center justify-center gap-1.5">
                  <div className="w-8 h-1 bg-neutral-800 rounded-full"></div>
                  <div className="w-2 h-2 bg-neutral-900 rounded-full"></div>
                </div>
                
                {/* Screen */}
                <div className="w-full h-full rounded-[32px] overflow-hidden bg-slate-100 relative z-10 flex flex-col pt-6">
                  {/* Mock Facebook Header */}
                  <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shadow-xs">
                    <div className="w-10 h-10 rounded-full bg-brand-orange text-warm-white flex items-center justify-center font-bold text-lg">
                      <i className="fa-solid fa-store text-base" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm leading-snug">Havneloppen</h4>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <i className="fa-brands fa-facebook text-brand-orange text-[10px]" /> 1.2k+ følgere
                      </p>
                    </div>
                  </div>

                  {/* Mock Facebook Posts Feed */}
                  <div className="p-3 space-y-3 overflow-y-auto flex-1 text-xs">
                    <div className="bg-white rounded-xl p-3 shadow-xs border border-slate-200 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-brand-orange text-white flex items-center justify-center text-xs font-bold">H</div>
                        <div>
                          <p className="font-semibold text-slate-800">Havneloppen</p>
                          <p className="text-[10px] text-slate-400">2 timer siden</p>
                        </div>
                      </div>
                      <p className="text-slate-700">Nye retro teaktræsmøbler & Holmegaard glas er lige ankommet i butikken i dag! 🏷️✨</p>
                      <div className="rounded-lg bg-warm-beige/60 p-6 text-center text-slate-500 font-medium border border-warm-beige">
                        <i className="fa-solid fa-camera text-slate-400 text-2xl mb-1 block" />
                        Nye loppefund på hylderne
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-3 shadow-xs border border-slate-200 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-brand-orange text-white flex items-center justify-center text-xs font-bold">H</div>
                        <div>
                          <p className="font-semibold text-slate-800">Havneloppen</p>
                          <p className="text-[10px] text-slate-400">I går kl. 14:15</p>
                        </div>
                      </div>
                      <p className="text-slate-700">Åbent i weekenden 10-16. Kom forbi Knudshovedvej 38 og gør et fantastisk fund! ☕☀️</p>
                    </div>
                  </div>

                  {/* Bottom CTA Overlay */}
                  <div className="p-3 bg-white border-t border-slate-200">
                    <a
                      href="https://www.facebook.com/p/Havneloppen-100063561880040/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center w-full py-2.5 bg-brand-orange text-warm-white font-bold rounded-lg hover:bg-brand-orange-hover transition-colors text-xs"
                    >
                      Åbn Facebook for at se flere opslag
                    </a>
                  </div>
                </div>

                {/* Home Indicator */}
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-24 h-1 bg-black/30 rounded-full z-30 pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Block Section */}
      <section className="py-16 md:py-24 bg-warm-beige/30">
        <div className="mx-auto max-w-7xl px-6">
          <div className="rounded-2xl bg-white border border-warm-beige shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-12">
            {/* Contact Info */}
            <div className="bg-brand-blue text-warm-white p-8 md:p-12 lg:col-span-5 flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-brand-orange">Havneloppen Nyborg</span>
                <h2 className="font-display text-3xl font-bold text-warm-white mt-2 mb-6">Kig forbi butikken</h2>
                <p className="text-warm-white/80 text-sm leading-relaxed mb-8">
                  Vi glæder os til at byde dig velkommen i vores hyggelige butik på havnen i Nyborg. Her kan du gå på opdagelse i ro og mag og gøre et fantastisk genbrugskup.
                </p>

                <ul className="space-y-5 text-sm">
                  <li className="flex items-start gap-3.5">
                    <i className="fa-solid fa-location-dot text-brand-orange text-lg mt-0.5" />
                    <div>
                      <strong className="block text-warm-white">Adresse</strong>
                      <span className="text-warm-white/80">Knudshovedvej 38, 5800 Nyborg</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3.5">
                    <i className="fa-solid fa-phone text-brand-orange text-lg mt-0.5" />
                    <div>
                      <strong className="block text-warm-white">Telefonnummer</strong>
                      <a href="tel:+4528767915" className="text-warm-white/80 hover:text-brand-orange transition-colors whitespace-nowrap">
                        28 76 79 15
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3.5">
                    <i className="fa-solid fa-envelope text-brand-orange text-lg mt-0.5" />
                    <div>
                      <strong className="block text-warm-white">E-mailadresse</strong>
                      <a href="mailto:havneloppen@gmail.com" className="text-warm-white/80 hover:text-brand-orange transition-colors whitespace-nowrap">
                        havneloppen@gmail.com
                      </a>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="border-t border-warm-white/10 pt-8 mt-8">
                <h4 className="font-display text-base font-bold text-brand-orange mb-3">Åbningstider</h4>
                <ul className="space-y-1.5 text-xs text-warm-white/80">
                  <li className="flex justify-between"><span>Mandag - Torsdag:</span> <span className="font-mono">10:00 - 18:00</span></li>
                  <li className="flex justify-between text-warm-white/40"><span>Fredag:</span> <span>Lukket</span></li>
                  <li className="flex justify-between"><span>Lørdag - Søndag og helligdage:</span> <span className="font-mono">10:00 - 16:00</span></li>
                </ul>
              </div>
            </div>

            {/* Map Placeholder or Atmospheric Image */}
            <div className="lg:col-span-7 relative min-h-[350px] bg-warm-beige">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/631586353-1484986060296776-2960624181118375474-n.jpg"
                alt="Havneloppen butik"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-orange/20 via-amber-500/15 to-yellow-400/5 mix-blend-multiply" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
